import { getAccessToken, getClientId, getUser, refreshTokens } from './authService';
import {
  API_KEY,
  CLIENT_APP,
  CLIENT_NAME,
  CLIENT_STAGES,
  SOURCE_APP_SLUG,
} from './clientConfig';

import { API_BASE as CONFIG_API_BASE } from './clientConfig';

export const API_BASE = CONFIG_API_BASE;
export const IDENTITY_SERVICE_BASE_URL = `${API_BASE}/identity-service`;
export const PROFILE_SERVICE_BASE_URL = `${API_BASE}/profile-service`;
export const TASK_SERVICE_BASE_URL = `${API_BASE}/olitask-service`;
export const PAYMENTS_SERVICE_BASE_URL = `${API_BASE}/payment-service`;
export const EMAIL_SERVICE_BASE_URL = `${API_BASE}/email-service`;

export { API_KEY, CLIENT_APP, CLIENT_NAME, CLIENT_STAGES, SOURCE_APP_SLUG };
/** @deprecated Use SOURCE_APP_SLUG */
export const MOBILE_SOURCE_APP = SOURCE_APP_SLUG;

type HeaderOptions = {
  actorId?: string | null;
  auth?: boolean;
  json?: boolean;
  acceptJson?: boolean;
};

type ServiceFetchOptions = RequestInit & HeaderOptions;

export type Page<T> = {
  content: T[];
  totalElements?: number;
  totalPages?: number;
  size?: number;
  number?: number;
  first?: boolean;
  last?: boolean;
  numberOfElements?: number;
  empty?: boolean;
};

export function getStoredActorId(): string | null {
  return getUser()?.id || null;
}

export function asBearer(token: string): string {
  return token.startsWith('Bearer ') ? token : `Bearer ${token}`;
}

async function buildHeaders(options: HeaderOptions = {}): Promise<Record<string, string>> {
  const { actorId, auth = true, json = true, acceptJson = false } = options;
  const headers: Record<string, string> = {
    'O-CLIENT-APP': CLIENT_APP,
    'O-API-KEY': API_KEY,
    'O-CLIENT-NAME': CLIENT_NAME,
  };

  if (json) headers['Content-Type'] = 'application/json';
  if (acceptJson) headers.Accept = 'application/json';

  const clientId = await getClientId();
  if (clientId) headers['O-CLIENT-ID'] = clientId;

  const resolvedActorId = actorId || getStoredActorId();
  if (resolvedActorId) headers['O-ACTOR-ID'] = resolvedActorId;

  if (auth) {
    const token = getAccessToken();
    if (!token) throw new Error('Not authenticated. Please sign in first.');
    headers.Authorization = asBearer(token);
  }

  return headers;
}

async function parseError(response: Response, fallback: string): Promise<string> {
  const text = await response.text().catch(() => '');
  if (!text) return fallback;

  try {
    const parsed = JSON.parse(text);
    return (
      parsed?.humanMessage ||
      parsed?.message ||
      parsed?.error ||
      parsed?.details ||
      fallback
    );
  } catch {
    return text || fallback;
  }
}

export async function serviceFetch(
  url: string,
  options: ServiceFetchOptions = {},
  fallbackMessage = 'Request failed'
): Promise<Response> {
  const { actorId, auth, json, acceptJson, headers, ...requestInit } = options;
  const builtHeaders = await buildHeaders({ actorId, auth, json, acceptJson });
  const mergedHeaders = {
    ...builtHeaders,
    ...(headers as Record<string, string> | undefined),
  };

  let response = await fetch(url, {
    ...requestInit,
    headers: mergedHeaders,
  });

  if ((response.status === 401 || response.status === 403) && getAccessToken()) {
    const refreshed = await refreshTokens();
    if (refreshed) {
      const retryHeaders = await buildHeaders({ actorId, auth, json, acceptJson });
      response = await fetch(url, {
        ...requestInit,
        headers: {
          ...retryHeaders,
          ...(headers as Record<string, string> | undefined),
        },
      });
    }
  }

  if (!response.ok) {
    throw new Error(await parseError(response, fallbackMessage));
  }

  return response;
}

export async function serviceJson<T>(
  url: string,
  options: ServiceFetchOptions = {},
  fallbackMessage = 'Request failed'
): Promise<T> {
  const response = await serviceFetch(url, options, fallbackMessage);
  const text = await response.text().catch(() => '');
  return (text ? JSON.parse(text) : null) as T;
}

export function normalizePage<T>(raw: T[] | Page<T> | null | undefined): Page<T> {
  if (!raw) return { content: [] };
  if (Array.isArray(raw)) return { content: raw };
  return { ...raw, content: Array.isArray(raw.content) ? raw.content : [] };
}
